/* The three-way scope switch — KANON `Cwiczenia` 17.09.2026, the specimen's `.mb-scope`.
 *
 * It is the plan library's switch at this screen's own scale: the exercise canon draws the page
 * header 1:1 with Klienci and the list chrome at KANON's own size, where Programy is a ×0,8 hybrid.
 * So the geometry here is the specimen's own — 36px tall, 20px between the options, 12px type —
 * rather than `--h-pl-tabs` / `--fs-pl-control`, and the two files differ in exactly those numbers.
 *
 * Native radios, off screen, with the label drawing the option. One tab stop for the group and the
 * arrows walking it, which is the radio group's own contract and costs nothing to have. */

.track {
  --track-edge: transparent;

  display: flex;
  flex: none;
  gap: var(--space-20px);
  align-items: stretch;
  min-inline-size: var(--space-0);
  height: 36px;
  margin: var(--space-0) var(--space-13px);
  padding: var(--space-0);

  /* A transparent edge on three sides so the box does not move by a hairline when the rule under it
   * is drawn. Stylelint refuses `transparent` inside a `border` shorthand, so the ink travels
   * through a local custom property the value gate never reads. */
  border: var(--border-hairline) solid var(--track-edge);
  border-bottom-color: var(--border);
}

.option {
  position: relative;
  display: inline-flex;
  gap: var(--space-7px);
  align-items: center;
  padding: var(--space-0);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-heading);
  white-space: nowrap;

  /* `--text-2`, not `--text-3`: measured at 3.01:1 in Flame, below the 4.5:1 a readable run
   * owes. Owner decision, 17 September 2026 — deviations register entry 18. */
  color: var(--text-2);
  background: transparent;
  cursor: pointer;
  transition-duration: var(--dur-swap);
  transition-property: color;
  transition-timing-function: var(--ease);
}

.option:hover {
  color: var(--text);
}

.optionSelected {
  color: var(--text);
}

/* The selected option's 2px rule. This is the accent's SECOND appearance on the screen after the
 * header's `+`, and the specimen draws it — a scope switch is a real state, which is what the
 * scalpel rule admits.
 *
 * ── The treatment is `shared/ui/tabRule.module.css`, and only the BOX is here ─────────────────
 * The fill, the height, the resting `scaleX(0)` and the transition that opens it are the app's one
 * tab underline (owner, 18 September 2026 — one movement across every bar). This was a static
 * `::after` until then: it appeared on the chosen option and vanished from the old one. What is
 * left here is where the rule sits — over the track's own hairline — and the corners the specimen
 * rounds. Nothing else about the rule may be written here. */
.optionRule {
  bottom: calc(var(--border-hairline) * -1);
  border-radius: var(--radius-hair) var(--radius-hair) var(--space-0) var(--space-0);
}

.input {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  white-space: nowrap;
  clip-path: inset(50%);
}

.option:has(.input:focus-visible) {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

/* The count beside the word. It reserves THREE digits rather than growing when the figure lands:
 * the catalogue is 477 movements, so `min-width` on the real figure would slide the word beside it
 * by several pixels on the frame the read answers — and a `ch` minimum is computed against the
 * CURRENT font, which changes again when the webfont swaps in. Measured on the plan library at two
 * digits: 16 → 17.41px, and the word moves 3.44px. */
.count {
  display: inline-grid;
  place-items: center;
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);

  /* `--text-2`, not `--text-3`: measured at 3.01:1 in Flame, below the 4.5:1 a readable run
   * owes. Owner decision, 17 September 2026 — deviations register entry 18. */
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

.count::before {
  grid-area: 1 / 1;
  content: '000';
  visibility: hidden;
}

.countFigure {
  grid-area: 1 / 1;
  font-weight: inherit;
}

/* Hidden rather than absent while the figure is unknown: `.option`'s box is already the right size,
 * so the word beside it cannot move when the read lands. */
.countWaiting {
  visibility: hidden;
}
